// Stencil
import {
  Component,
  Element,
  getAssetPath,
  h,
  Host,
  Prop,
  State,
  Watch
} from "@stencil/core";

// Other Libraries
import { ComboBoxModel } from "@genexus/chameleon-controls-library";
import { MercuryBundles } from "@genexus/mercury";

// Custom Imports
import { config } from "../../../common/config";
import {
  ControlValidation,
  validateControls
} from "../../../common/form-validation";
import { Locale } from "../../../common/locale";
import { FormSubmitResult } from "../../../common/types";
import { mapAuthenticationTypeToComboBoxModel } from "./helpers";
import { NewServerConnectionData } from "../server-selector/server-selector";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tooltip",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-connect-gx-server",
  styleUrl: "connect-gx-server.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/connect-gx-server"]
})
export class HTMLGxIdeConnectGxServer {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  @Element() el: HTMLGxIdeConnectGxServerElement;
  #usernameRef!: HTMLChEditElement;
  #passwordRef!: HTMLChEditElement;
  #serverUrlRef!: HTMLChEditElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/connect-gx-server/shortcuts.json`
  );

  @State() authenticationTypesComboBoxModel: ComboBoxModel;
  @State() gxServerConnectionData: GXServerConnectionData = {
    authenticationType: {
      id: null,
      name: null
    },
    serverUrl: null,
    userName: null,
    userPassword: null,
    serverName: null
  };
  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * List of authentication types.
   */
  @Prop() readonly authenticationTypes!: AuthenticationType[];
  @Watch("authenticationTypes")
  authenticationTypesChanged(newAuthenticationTypes: AuthenticationType[]) {
    if (newAuthenticationTypes?.length) {
      this.authenticationTypesComboBoxModel =
        mapAuthenticationTypeToComboBoxModel(newAuthenticationTypes);
    }
  }

  /**
   * Callback that must be invoked when the 'Cancel' button is pressed.
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * Callback that must be invoked when the 'Connect' button is pressed.
   */
  @Prop() readonly connectCallback!: (
    data: GXServerConnectionData
  ) => Promise<FormSubmitResult>;

  /**
   * Initial user values for the case in which the user returns to the
   * login modal after successful authentication.
   */
  @Prop() readonly defaultConnectionData?: NewServerConnectionData;

  /**
   * true if the user should be allowed to manually enter a server URL.
   */
  @Prop() readonly enableCustomServer: boolean = false;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    this.#init();
  }

  async componentDidLoad() {
    this.#initializeValidatableControls();
  }

  #cancelClickHandler = () => {
    this.cancelCallback();
  };

  #connectClickHandler = async () => {
    this.connectCallback(this.gxServerConnectionData).then(
      (formSubmitResult: FormSubmitResult) => {
        this.validatableControls = validateControls(
          formSubmitResult,
          this.validatableControls
        );
      }
    );
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on other dialogs as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #init = () => {
    this.authenticationTypesChanged(this.authenticationTypes);
    // set initial values:
    // server url
    this.gxServerConnectionData.serverUrl =
      this.defaultConnectionData?.serverUrl;
    //server name
    this.gxServerConnectionData.serverName =
      this.defaultConnectionData?.serverName;
    // authentication type
    this.gxServerConnectionData.authenticationType = {
      id: this.authenticationTypesComboBoxModel[0]?.value,
      name: this.authenticationTypesComboBoxModel[0]?.caption
    };
  };

  #initializeValidatableControls = () => {
    // populate #controlsValidation with the controls that could have errors.
    const validatableControls: HTMLElement[] = [
      this.#usernameRef,
      this.#passwordRef,
      this.#serverUrlRef
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #authenticationInputEventHandler = (
    event: CustomEvent<string> | InputEvent
  ) => {
    const authenticationTypeId = event.detail as string;
    const authenticationTypeIndex = this.authenticationTypes.findIndex(
      authType => {
        return authType.id === authenticationTypeId;
      }
    );
    const authenticationTypeName =
      this.authenticationTypes[authenticationTypeIndex].name;

    this.gxServerConnectionData.authenticationType = {
      id: authenticationTypeId,
      name: authenticationTypeName
    };
  };
  #usernameInputEventHandler = (event: CustomEvent<string> | InputEvent) => {
    this.gxServerConnectionData.userName = event.detail as string;
  };
  #passwordInputEventHandler = (event: CustomEvent<string> | InputEvent) => {
    this.gxServerConnectionData.userPassword = event.detail as string;
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>

        <section class="section">
          <div class="main field-group spacing-body">
            <div
              // server urls
              class="field field-block server-url"
            >
              <label class="label" htmlFor="server-url">
                {this.#componentLocale.main.serverURLLabel}
              </label>
              <ch-edit
                type="url"
                id="server-url"
                class="input"
                value={this.gxServerConnectionData?.serverUrl}
                readonly={true}
                ref={(el: HTMLChEditElement) =>
                  (this.#serverUrlRef = el as HTMLChEditElement)
                }
              ></ch-edit>
            </div>

            <div
              // authentication types
              class="field field-block authentication-type"
            >
              <label class="label" htmlFor="authentication-type">
                {this.#componentLocale.main.authenticationTypeLabel}
              </label>
              <ch-combo-box-render
                id="authentication-type"
                class="combo-box"
                accessibleName="Authentication Types"
                model={this.authenticationTypesComboBoxModel}
                placeholder="Select an authentication type"
                value={this.gxServerConnectionData?.authenticationType.id}
                onInput={this.#authenticationInputEventHandler}
              ></ch-combo-box-render>
            </div>

            <div
              // username
              class="field field-block user-name"
            >
              <label class="label" htmlFor="username">
                {this.#componentLocale.main.usernameLabel}
              </label>
              <ch-edit
                type="text"
                id="username"
                class="input"
                value={this.gxServerConnectionData?.userName}
                onInput={this.#usernameInputEventHandler}
                ref={(el: HTMLChEditElement) =>
                  (this.#usernameRef = el as HTMLChEditElement)
                }
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#usernameRef)}
            </div>

            <div
              // user password
              class="field field-block user-password"
            >
              <label class="label" htmlFor="password">
                {this.#componentLocale.main.passwordLabel}
              </label>
              <ch-edit
                type="password"
                id="password"
                class="input"
                value={this.gxServerConnectionData?.userPassword}
                onInput={this.#passwordInputEventHandler}
                ref={(el: HTMLChEditElement) =>
                  (this.#passwordRef = el as HTMLChEditElement)
                }
                showPasswordButton={true}
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#passwordRef)}
            </div>
          </div>

          <footer class="dialog-footer-with-border spacing-body">
            <div class="buttons-spacer">
              <button
                id="cancel-button"
                // cancel button
                class="button-secondary"
                onClick={this.#cancelClickHandler}
              >
                {this.#componentLocale.footer.backButtonCaption}
              </button>

              <button
                id="connect-button"
                // connect button
                class="button-primary"
                onClick={this.#connectClickHandler}
              >
                {this.#componentLocale.footer.connectButtonCaption}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type GXServerConnectionData = {
  authenticationType: AuthenticationType;
  serverUrl: string;
  userName: string;
  userPassword: string;
  serverName?: string;
};

export type AuthenticationType = {
  id: string;
  name: string;
};
